import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import axios from 'axios';

interface User {
  id: string;
  email: string;
  plan: string;
  role?: string;
  stripeCustomerId?: string;
  subscriptionId?: string;
  subscriptionStatus: string;
  createdAt: string;
  emailVerified: boolean;
  lastLogin?: string;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const initializedRef = useRef(false); // ← useRef statt useState

  // Configure axios defaults
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.pfaendungstabelle.voidmind.io';
  
  useEffect(() => {
    // Prevent double initialization (React StrictMode protection)
    if (initializedRef.current) {
      // console.log('AuthProvider already initialized, skipping...');
      return;
    }
    
    // console.log('Setting up AuthProvider with API_BASE_URL:', API_BASE_URL);
    
    // Set up axios interceptors
    axios.defaults.baseURL = API_BASE_URL;
    
    // Add auth token to requests
    const requestInterceptor = axios.interceptors.request.use((config) => {
      const token = localStorage.getItem('authToken');
      // console.log('Request interceptor - token exists:', !!token);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      // console.log('Making request to:', config.url, 'with headers:', config.headers);
      return config;
    });

    // Handle auth errors
    const responseInterceptor = axios.interceptors.response.use(
      (response) => {
        // console.log('Response received:', response.status, response.data);
        return response;
      },
      (error) => {
        // console.log('Response error:', error.response?.status, error.response?.data);
        if (error.response?.status === 401) {
          // console.log('401 error - clearing auth token');
          localStorage.removeItem('authToken');
          setUser(null);
        }
        return Promise.reject(error);
      }
    );

    // Check for existing auth token
    checkAuthStatus();
    
    // Mark as initialized
    initializedRef.current = true;

    // Cleanup function to remove interceptors
    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [API_BASE_URL]); // ← initialized aus Dependencies entfernt

  const checkAuthStatus = async () => {
    // console.log('=== AUTH STATUS CHECK ===');
    const token = localStorage.getItem('authToken');
    // console.log('Token exists:', !!token);
    // console.log('Token value:', token?.substring(0, 20) + '...');
    // console.log('API Base URL:', API_BASE_URL);
    
    if (token) {
      try {
        // Check if token is expired before making request
        try {
          // JWT Token richtig dekodieren - nur den Payload-Teil
          const tokenParts = token.split('.');
          if (tokenParts.length !== 3) {
            // console.log('Invalid JWT format - not 3 parts');
            throw new Error('Invalid token format');
          }
          
          const payload = JSON.parse(atob(tokenParts[1])); // ← Nur Payload dekodieren
          // console.log('Token payload:', payload);
          // console.log('Token exp:', payload.exp, 'Current time:', Date.now());
          
          // JWT exp ist in Sekunden, Date.now() in Millisekunden
          if (payload.exp && payload.exp * 1000 < Date.now()) { // ← * 1000 für Millisekunden
            // console.log('Token is expired');
            localStorage.removeItem('authToken');
            setUser(null);
            setLoading(false);
            return;
          }
          
          // console.log('Token is not expired, proceeding with validation');
        } catch (tokenError) {
          // console.log('Token decode error:', tokenError);
          localStorage.removeItem('authToken');
          setUser(null);
          setLoading(false);
          return;
        }

        // Verify token is still valid by checking user info
        // console.log('Validating token with backend at:', `${API_BASE_URL}/auth/me`);
        const response = await axios.get('/auth/me');
        // console.log('Auth validation response status:', response.status);
        // console.log('Auth validation response data:', response.data);
        
        if (response.data.success && response.data.user) {
          // console.log('User authenticated successfully:', response.data.user.email);
          setUser(response.data.user);
        } else {
          // console.log('Invalid auth response, clearing token');
          localStorage.removeItem('authToken');
          setUser(null);
        }
      } catch (error: any) {
        // console.log('Auth validation error:', error.message, error.code);
        
        // If it's a network error (backend not running), don't clear the token
        // The user should stay logged in until the backend is available again
        if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
          // console.log('Network error - using cached user data');
          // Decode token to get user info
          try {
            const tokenParts = token.split('.');
            const payload = JSON.parse(atob(tokenParts[1])); // ← Korrigierte Dekodierung
            const cachedUser = {
              id: payload.userId,
              email: payload.email,
              plan: payload.plan,
              subscriptionStatus: 'active',
              createdAt: new Date().toISOString(),
              emailVerified: true
            };
            setUser(cachedUser);
          } catch (decodeError) {
            // console.log('Failed to decode token for offline use:', decodeError);
            localStorage.removeItem('authToken');
            setUser(null);
          }
        } else {
          // Other errors (401, 403, etc.) should clear the token
          // console.log('Non-network error - clearing auth token');
          localStorage.removeItem('authToken');
          setUser(null);
        }
      }
    } else {
      // console.log('No token found');
      setUser(null);
    }
    setLoading(false);
    // console.log('=== AUTH STATUS CHECK COMPLETE ===');
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await axios.post('/auth/login', {
        email,
        password,
      });

      if (response.data.success && response.data.token && response.data.user) {
        const { token, user: userData } = response.data;
        localStorage.setItem('authToken', token);
        setUser(userData);
      } else {
        throw new Error('Invalid login response');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Login failed');
    }
  };

  const register = async (email: string, password: string) => {
    try {
      const response = await axios.post('/auth/register', {
        email,
        password,
      });

      if (response.data.success && response.data.token && response.data.user) {
        const { token, user: userData } = response.data;
        localStorage.setItem('authToken', token);
        setUser(userData);
      } else {
        throw new Error('Invalid registration response');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Registration failed');
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get('/auth/me');
      if (response.data.success && response.data.user) {
        setUser(response.data.user);
      } else {
        // Invalid response, user might be logged out
        localStorage.removeItem('authToken');
        setUser(null);
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      // On error, clear auth state
      localStorage.removeItem('authToken');
      setUser(null);
    }
  };

  const value = {
    user,
    login,
    register,
    logout,
    loading,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
